import styled, { css, keyframes } from 'styled-components';
import { colors, getMediumRGBA } from '../../../../styles/colors';
import { fontFamily, radii, space, text } from '../../../../styles/onboardingUiTokens';
import { SvgIcon } from '../icons/SvgIcon';
import { formFieldBorderOpacity, formFieldBorderWidthPx, formFieldControlMinHeightPx } from './formFieldTokens';

/** Where optional `hint` copy is rendered relative to the control. Default: `below`. */
export type FormHintPlacement = 'above' | 'below';

export const DEFAULT_FORM_HINT_PLACEMENT: FormHintPlacement = 'below';

export const formControlSurfaceCss = css`
  width: 100%;
  box-sizing: border-box;
  min-height: ${formFieldControlMinHeightPx}px;
  border-radius: ${radii.control};
  border: ${formFieldBorderWidthPx}px solid ${getMediumRGBA(formFieldBorderOpacity)};
  background-color: ${colors.white};
  color: ${colors.dark};
  padding: ${space.sm} ${space.md};
  font-family: ${fontFamily.ubuntu};
  font-size: ${text.formControl.fontSize};
  line-height: ${text.formControl.lineHeight};
  font-weight: 400;
  outline: none;
  box-shadow: none;
  transition: border-color 160ms ease;

  &::placeholder {
    color: ${colors.dark};
    opacity: 0.45;
  }

  &:hover:not(:disabled):not(:read-only) {
    border-color: ${getMediumRGBA(0.3)};
  }

  &:focus {
    border-color: ${colors.primary};
  }

  /* The second selector outranks the hover rule above; a focused field keeps the primary border. */
  &[aria-invalid='true']:not(:focus),
  &[aria-invalid='true']:hover:not(:disabled):not(:read-only):not(:focus) {
    border-color: ${colors.lightRed};
  }

  &:focus-visible {
    outline: 2px solid ${colors.primary};
    outline-offset: 2px;
  }

  &:disabled,
  &:read-only {
    opacity: 0.55;
    cursor: not-allowed;
  }

  /*
   * iOS/WebKit lays a date input out from its own shadow content rather than from the width
   * we set, so the field overflowed the viewport, and it centres the value while every other control
   * here is left-aligned. Dropping the native appearance and zeroing the inner pseudo-elements puts
   * it back in line on WebKit; on Chromium these rules are inert apart from the alignment, and the
   * calendar picker indicator is left alone (PF-4441).
   */
  &[type='date'] {
    -webkit-appearance: none;
    appearance: none;
    min-width: 0;
    max-width: 100%;
  }

  &[type='date']::-webkit-date-and-time-value {
    text-align: left;
    margin: 0;
    min-width: 0;
  }

  &[type='date']::-webkit-datetime-edit {
    padding: 0;
    text-align: left;
  }
`;

export const FormTextControl = styled.input<{ $trailingPadding?: string }>`
  ${formControlSurfaceCss}
  ${({ $trailingPadding }) => ($trailingPadding ? `padding-right: ${$trailingPadding};` : '')}
`;

export const FormIbanControl = styled.input<{ $trailingPadding?: string }>`
  ${formControlSurfaceCss}
  font-family: ${fontFamily.ubuntuMono};
  ${({ $trailingPadding }) => ($trailingPadding ? `padding-right: ${$trailingPadding};` : '')}
`;

export const FormFieldRoot = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: ${space.xs};
`;

export { GroupLabelLegend, Label } from './Label';

export const FormFieldHint = styled.p`
  margin: 0;
  font-size: ${text.caption.fontSize};
  line-height: ${text.caption.lineHeight};
  color: ${getMediumRGBA(0.9)};
`;

type FormFieldHintMaybeProps = {
  id: string;
  hint?: string;
  placement: FormHintPlacement;
  slot: FormHintPlacement;
};

/** Renders `FormFieldHint` in the `above` or `below` slot; use twice in the field layout with different `slot`. */
export function FormFieldHintMaybe({ id, hint, placement, slot }: FormFieldHintMaybeProps) {
  const trimmed = hint?.trim();
  if (!trimmed || placement !== slot) {
    return null;
  }
  return <FormFieldHint id={id}>{trimmed}</FormFieldHint>;
}

type FormFieldErrorProps = {
  id?: string;
  message?: string;
};

export function FormFieldError({ id, message }: FormFieldErrorProps) {
  const trimmed = message?.trim();
  if (!trimmed) {
    return null;
  }
  return (
    <ErrorText id={id} role="alert">
      <ErrorIcon>
        <SvgIcon name="warning" size={16} primaryColor={colors.lightRed} />
      </ErrorIcon>
      <span>{trimmed}</span>
    </ErrorText>
  );
}

const errorEnter = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: none;
  }
`;

const ErrorText = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${space.xs};
  font-size: ${text.caption.fontSize};
  line-height: ${text.caption.lineHeight};
  color: ${colors.errorText};
  animation: ${errorEnter} 160ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const ErrorIcon = styled.span`
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  height: ${text.caption.lineHeight};
`;

export const VisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
